import {
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AbsHistory from "../../abstract/History";
import { SortType } from "../../abstract/types";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { useWebSocket, WebSocketEvent } from "../../contexts/WebSocketContext";
import { colors, fontSizes } from "../../theme";
import { formatDate, scoreColor } from "../../utils";
import { GetCycleResponse, GetCycleResponseItem } from "./types";

const History: React.FC = () => {
  const { updateActions, updateBreadcrumbs, translate } = useApp();
  const navigate = useNavigate();
  const { apiService } = useApi();
  const { session } = useAuth();
  const [isSearching, setIsSearching] = useState(false);

  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [data, setData] = useState<GetCycleResponseItem[] | null>(null);
  const { showError, showSuccess } = useError();
  const [disabledId, setDisabledId] = useState<string | null>(null);

  const translations = useMemo(() => translate("embryo"), [translate]);

  const location = useLocation();
  const newId = location.state?.newId;

  const websocket = useWebSocket();

  const handleEvent = useCallback(
    (event: WebSocketEvent) => {
      if (event.name === "cycle_done") {
        showSuccess(translations.history.messages.doneAnalyzingCycle);
        refetchItem(event.data.cycle);
        navigate(location.pathname, {
          replace: true,
          state: { ...location.state, newId: undefined },
        });
      }
    },
    [showSuccess, navigate, location.pathname, location.state, data]
  );

  useEffect(() => {
    websocket.register("embryo", handleEvent);
    return () => {
      websocket.unregister("embryo", handleEvent);
    };
  }, [data]);

  useEffect(() => {
    if (newId && session) {
      setDisabledId(newId);

      const to = setTimeout(() => {
        if (disabledId !== null) {
          setDisabledId(null);
          // refetchItem(newId);
          navigate(location.pathname, {
            replace: true,
            state: { ...location.state, newId: undefined },
          });
          showSuccess(translations.history.messages.doneAnalyzingCycle);
          console.log("Done with timeout");
        }

        return () => clearTimeout(to);
      }, 60000);
    }
  }, [newId, session]);

  const handleOpenItem = (row: GetCycleResponseItem) => {
    if (disabledId === row.id) {
      return;
    }
    navigate(`/embryo/${row.id}`);
  };

  useEffect(() => {
    if (data?.length) {
      updateActions([
        <Button
          key={"create"}
          color="blackButton"
          onClick={() => navigate("/embryo/create")}
        >
          {" "}
          {translations.history.buttons.addNewCycle}{" "}
        </Button>,
      ]);
    }
    updateBreadcrumbs([
      {
        label: translations.breadcrumbs.embryoAssessment,
        onClick: () => {
          navigate("/embryo");
        },
      },
    ]);

    return () => {
      updateActions([]);
      updateBreadcrumbs([]);
    };
  }, [data]);

  const onSearch = (
    sort: SortType<
      GetCycleResponseItem & { topEmbryoScores: number; actualOutcome: string }
    > | null,
    patient: string,
    review: boolean,
    nextPageToken?: string
  ) => {
    get(sort, patient, review, nextPageToken)
      .then((data) => {
        // setCount((prev) => prev + data.length);
        setData(data ?? []);
      })
      .catch((error) => {
        setData([]);
        showError(error);
      });
  };

  const refetchItem = useCallback(
    async (id: string) => {
      if (!data) {
        return;
      }
      try {
        let cData = [...(data ?? [])];
        const cycle = await apiService.get<GetCycleResponse>(`/embryo/${id}`);
        const index = cData.findIndex((c) => c.id === id);
        if (index !== -1) {
          cData[index] = cycle.cycle;
          setData(cData);
          setDisabledId(null);
        }
      } catch (error) {
        showError(translations.history.messages.errorFetchingData);
      }
    },
    [data]
  );

  const get = useCallback(
    async (
      sort: SortType<
        GetCycleResponseItem & {
          topEmbryoScores: number;
          actualOutcome: string;
        }
      > | null = null,
      patient: string = "",
      review: boolean = false,
      pageToken?: string
    ) => {
      console.log("get-Fetching data", sort, patient, review, pageToken);
      const params = new URLSearchParams();
      if (sort) {
        params.append("sort", `${sort.column},${sort.direction}`);
      }
      if (patient) {
        params.append("patient", patient);
      }
      if (review) {
        params.append("review", "1");
      }
      if (pageToken) {
        params.append("page_token", pageToken);
      }

      setIsSearching(true);

      try {
        const response = await apiService.get<{
          next_page_token: string | null;
          cycles: GetCycleResponseItem[];
        }>(`/embryo?${params.toString()}`);

        if (response.next_page_token) {
          setNextPageToken(response.next_page_token);
        }

        console.log(`response`, response);
        return response.cycles;
      } catch (error) {
        console.error(error);
        setData([]);
        showError(translations.history.errors.errorFetchingData);
      } finally {
        setIsSearching(false);
      }
    },
    []
  );

  const getOutcomeText = (cycle: GetCycleResponseItem) => {
    if (cycle.u_transfer_date && cycle.u_transfer_outcome) {
      return cycle.u_transfer_outcome
        ? translations.history.outcome.successfulImplantation
        : translations.history.outcome.successfulImplantation;
    }

    return translations.history.outcome.reviewRequired;
  };

  const topEmbryoScores = (cycle: GetCycleResponseItem) => {
    if (disabledId === cycle.id) {
      return (
        <Chip
          sx={{
            backgroundColor: colors.stone[100],
            fontWeight: "normal",
            fontSize: fontSizes.sm,
            color: colors.stone[900],
          }}
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress
                size={"20px"}
                color="inherit"
                sx={{ color: colors.stone[900] }}
              />
              <Typography
                fontSize={fontSizes.sm}
                fontWeight={"bold"}
                color={colors.stone[900]}
              >
                Processing the result
              </Typography>
            </Stack>
          }
        />
      );
    }
    if (cycle.p_overall) {
      return (
        <Chip
          sx={{
            backgroundColor: scoreColor(cycle.p_overall),
            fontWeight: "normal",
            fontSize: fontSizes.sm,
            color: colors.stone[900],
          }}
          label={`${cycle.p_overall}`}
        />
      );
    }
    return null;
  };

  return (
    <AbsHistory<
      GetCycleResponseItem & { topEmbryoScores: number; actualOutcome: string }
    >
      isLoadingData={isSearching}
      disabledId={disabledId}
      title={translations.history.title}
      columns={[
        {
          id: "id",
          label: translations.history.columns.cycleId,
          sortable: true,
        },
        {
          id: "patient",
          label: translations.history.columns.patientName,
          sortable: true,
        },
        {
          id: "created",
          label: translations.history.columns.cycleDate,
          sortable: true,
        },
        {
          id: "topEmbryoScores",
          label: translations.history.columns.topEmbryoScores,
          width: 210,
        },
      ]}
      onSearch={onSearch}
      data={
        data
          ? (data.map((cycle) => ({
              ...cycle,
              created: formatDate(new Date(cycle.created)),
              actualOutcome: getOutcomeText(cycle),
              topEmbryoScores: topEmbryoScores(cycle),
            })) as any)
          : null
      }
      onItemClick={handleOpenItem}
    />
  );
};

export default History;
